import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { 
  Lock, 
  Search, 
  Download, 
  User, 
  Briefcase, 
  Building, 
  Mail, 
  Phone, 
  Calendar, 
  LogOut, 
  ArrowLeft,
  Award,
  Sparkles,
  Users
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(() => localStorage.getItem("somewhere_admin_token") || "");
  const [authenticated, setAuthenticated] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest"); // "newest" or "oldest"

  useEffect(() => {
    if (token) {
      fetchWaitlist(token);
    }
  }, [token]);

  const fetchWaitlist = async (authToken) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/waitlist`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Cache-Control": "no-cache",
        },
        params: { _t: Date.now() }
      });
      setSubmissions(response.data);
      setAuthenticated(true);
      setAuthError("");
    } catch (err) {
      console.error(err);
      // If unauthorized, clear token
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("somewhere_admin_token");
        setToken("");
        setAuthenticated(false);
      } else {
        toast.error("Failed to load waitlist data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) {
      setAuthError("Please enter the admin password");
      return;
    }

    setLoading(true);
    setAuthError("");
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      const newToken = response.data.token;
      localStorage.setItem("somewhere_admin_token", newToken);
      setToken(newToken);
      toast.success("Welcome back, Administrator");
    } catch (err) {
      console.error(err);
      const errorMsg = err.response?.data?.detail || "Incorrect admin password";
      setAuthError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("somewhere_admin_token");
    setToken("");
    setAuthenticated(false);
    setSubmissions([]);
    toast.success("Logged out successfully");
  };

  const handleCSVExport = () => {
    // We can directly download the CSV file from backend
    const downloadUrl = `${API}/admin/waitlist/csv?token=${token}`;
    window.open(downloadUrl, "_blank");
    toast.success("CSV export initiated");
  };

  // Filter and sort submissions
  const filteredSubmissions = submissions.filter((item) => {
    const q = searchTerm.toLowerCase();
    return (
      item.full_name?.toLowerCase().includes(q) ||
      item.company?.toLowerCase().includes(q) ||
      item.designation?.toLowerCase().includes(q) ||
      item.email?.toLowerCase().includes(q) ||
      item.phone?.toLowerCase().includes(q)
    );
  });

  // Sort submissions based on newest vs oldest (submissions are already reversed or ascending from API)
  const sortedSubmissions = [...filteredSubmissions].sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return sortBy === "newest" ? dateB - dateA : dateA - dateB;
  });

  // Chronological rank (oldest = #1), independent of the display sort order.
  // Keyed by email (unique per signup) so rank stays stable regardless of sorting.
  const rankByEmail = new Map();
  [...submissions]
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    .forEach((s, i) => rankByEmail.set(s.email, i + 1));

  const formatDate = (dateStr) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short"
      });
    } catch (e) {
      return dateStr;
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#0B0B0F] text-white flex flex-col justify-center items-center px-6 relative font-inter">
        <Toaster position="top-center" theme="dark" richColors />
        
        {/* Absolute design accents */}
        <div className="absolute top-1/4 left-1/2 transform -translate-x-1/2 w-[300px] h-[300px] bg-radial-gradient from-[#C8A24A]/10 to-transparent pointer-events-none rounded-full blur-[100px]" />
        
        <div className="mb-10 text-center">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-[#B0B0B0] hover:text-[#C8A24A] uppercase tracking-widest transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Return to Site
          </Link>
        </div>

        {/* Lock Gate Container */}
        <div 
          data-testid="admin-login-form"
          className="w-full max-w-md bg-[#121217] border border-white/5 rounded-2xl p-8 shadow-[0_20px_50px_rgba(0,0,0,0.5)] space-y-6 relative z-10"
        >
          <div className="text-center space-y-2">
            <div className="w-12 h-12 rounded-full bg-[#C8A24A]/5 border border-[#C8A24A]/20 flex items-center justify-center mx-auto text-[#C8A24A] mb-4">
              <Lock className="w-5 h-5" />
            </div>
            <h2 className="text-2xl font-semibold font-clash text-white tracking-wide">Secure Admin Access</h2>
            <p className="text-xs text-[#B0B0B0] tracking-wider uppercase">SOMEWHERE Private Waitlist Control</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-1.5 text-left">
              <label className="text-xs uppercase tracking-widest text-[#B0B0B0] font-semibold">
                Secret Passphrase
              </label>
              <input 
                type="password"
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                className="w-full bg-black/40 border border-white/10 focus:border-[#C8A24A] hover:border-white/20 transition-all rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#C8A24A]"
              />
            </div>

            {authError && (
              <p className="text-xs text-red-400 text-left bg-red-950/20 border border-red-900/30 rounded px-3 py-2">
                {authError}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-password-submit"
              className="w-full bg-[#C8A24A] text-[#0B0B0F] hover:bg-[#DCC278] disabled:opacity-50 font-bold rounded-lg px-4 py-3.5 transition-all duration-300 text-xs tracking-widest uppercase"
            >
              {loading ? "Decrypting..." : "Authenticate"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-white font-inter flex flex-col">
      <Toaster position="top-center" theme="dark" richColors />

      {/* Admin Header */}
      <header className="sticky top-0 z-40 bg-[#0F0F14]/90 border-b border-white/5 backdrop-blur-md px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex flex-col text-left">
              <span className="text-base font-semibold tracking-widest font-clash text-white">SOMEWHERE CONTROL ROOM</span>
              <span className="text-[9px] uppercase tracking-widest text-[#C8A24A] font-semibold -mt-0.5">Founding Waitlist Registry</span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs text-[#B0B0B0] hover:text-[#C8A24A] bg-white/3 border border-white/5 px-4 py-2 rounded-full transition-colors flex items-center gap-1.5">
              <ArrowLeft className="w-3.5 h-3.5" /> View Landing
            </Link>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="text-xs text-red-400 hover:text-red-300 bg-red-950/20 border border-red-900/20 px-4 py-2 rounded-full transition-colors flex items-center gap-1.5"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Control Dashboard */}
      <main className="flex-1 max-w-7xl w-full mx-auto p-6 md:p-8 space-y-8">
        
        {/* Key Metrics row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="bg-[#121217] border border-white/5 rounded-xl p-6 text-left flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#C8A24A]/10 border border-[#C8A24A]/20 flex items-center justify-center text-[#C8A24A]">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <span className="text-xs text-[#8A8A8A] uppercase tracking-wider block">Total Applications</span>
              <span 
                data-testid="admin-total-count"
                className="text-2xl font-bold text-white font-clash"
              >
                {submissions.length}
              </span>
            </div>
          </div>

          <div className="bg-[#121217] border border-white/5 rounded-xl p-6 text-left flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center text-yellow-500">
              <Award className="w-5 h-5" />
            </div>
            <div>
              <span className="text-xs text-[#8A8A8A] uppercase tracking-wider block">Founding Members</span>
              <span className="text-2xl font-bold text-white font-clash">
                {Math.min(submissions.length, 500)} <span className="text-xs text-[#8A8A8A] font-light">/ 500</span>
              </span>
            </div>
          </div>

          <div className="bg-[#121217] border border-white/5 rounded-xl p-6 text-left flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-500">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <span className="text-xs text-[#8A8A8A] uppercase tracking-wider block">Membership Rate</span>
              <span className="text-2xl font-bold text-white font-clash">100% Private</span>
            </div>
          </div>
        </div>

        {/* Search, Sort and Export Toolbar */}
        <div className="bg-[#121217] border border-white/5 rounded-xl p-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#8A8A8A]" />
            <input 
              type="text"
              placeholder="Search by name, company, email, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="admin-search-input"
              className="w-full bg-black/40 border border-white/10 hover:border-white/20 transition-all rounded-lg pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#C8A24A]"
            />
          </div>

          <div className="flex w-full md:w-auto items-center justify-end gap-3">
            {/* Sort Button */}
            <div className="flex items-center gap-1.5 bg-black/40 border border-white/10 rounded-lg px-3 py-1.5">
              <span className="text-xs text-[#8A8A8A]">Sort:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                data-testid="admin-sort-button"
                className="bg-transparent text-xs text-white focus:outline-none cursor-pointer"
              >
                <option value="newest" className="bg-[#121217]">Newest First</option>
                <option value="oldest" className="bg-[#121217]">Oldest First</option>
              </select>
            </div>

            {/* CSV Export Button */}
            <button
              onClick={handleCSVExport}
              data-testid="admin-csv-export-button"
              className="bg-[#C8A24A] text-[#0B0B0F] hover:bg-[#DCC278] text-xs font-semibold rounded-lg px-4 py-2.5 transition-colors flex items-center gap-2"
            >
              <Download className="w-3.5 h-3.5" /> Export Waitlist CSV
            </button>
          </div>
        </div>

        {/* Submissions Dense Table */}
        <div className="bg-[#121217] border border-white/5 rounded-xl overflow-hidden shadow-xl">
          <div className="overflow-x-auto">
            <table 
              data-testid="admin-waitlist-table"
              className="w-full text-left border-collapse"
            >
              <thead>
                <tr className="border-b border-white/10 bg-black/20 text-xs text-[#8A8A8A] uppercase tracking-wider">
                  <th className="py-4 px-6 font-medium">Rank</th>
                  <th className="py-4 px-6 font-medium">Full Name</th>
                  <th className="py-4 px-6 font-medium">Company</th>
                  <th className="py-4 px-6 font-medium">Designation</th>
                  <th className="py-4 px-6 font-medium">Email Address</th>
                  <th className="py-4 px-6 font-medium">Phone Number</th>
                  <th className="py-4 px-6 font-medium">Submission Date/Time</th>
                  <th className="py-4 px-6 font-medium text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 text-sm">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="py-12 text-center text-xs text-[#8A8A8A]">
                      Retrieving waitlist entries from secure vault...
                    </td>
                  </tr>
                ) : sortedSubmissions.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="py-12 text-center text-xs text-[#8A8A8A]">
                      No waitlist submissions match your search query.
                    </td>
                  </tr>
                ) : (
                  sortedSubmissions.map((item, index) => {
                    // Chronological rank (oldest = #1), from the email-keyed map above.
                    const rank = rankByEmail.get(item.email) ?? (index + 1);
                    const isFounding = rank <= 500;

                    return (
                      <tr 
                        key={item._id || item.id}
                        className={`hover:bg-white/2 transition-colors ${
                          isFounding ? "bg-[#C8A24A]/2" : ""
                        }`}
                      >
                        <td className="py-4 px-6 font-mono text-xs text-[#8A8A8A]">
                          #{rank}
                        </td>
                        <td className="py-4 px-6 font-medium text-white flex items-center gap-2">
                          <User className="w-3.5 h-3.5 text-[#C8A24A] opacity-60" />
                          {item.full_name}
                        </td>
                        <td className="py-4 px-6 text-[#B0B0B0]">
                          <div className="flex items-center gap-1.5">
                            <Building className="w-3.5 h-3.5 text-white/30" />
                            {item.company}
                          </div>
                        </td>
                        <td className="py-4 px-6 text-[#B0B0B0]">
                          <div className="flex items-center gap-1.5">
                            <Briefcase className="w-3.5 h-3.5 text-white/30" />
                            {item.designation}
                          </div>
                        </td>
                        <td className="py-4 px-6 text-[#B0B0B0] font-mono text-xs">
                          <div className="flex items-center gap-1.5">
                            <Mail className="w-3.5 h-3.5 text-white/30" />
                            {item.email}
                          </div>
                        </td>
                        <td className="py-4 px-6 text-[#B0B0B0] font-mono text-xs">
                          <div className="flex items-center gap-1.5">
                            <Phone className="w-3.5 h-3.5 text-white/30" />
                            {item.phone}
                          </div>
                        </td>
                        <td className="py-4 px-6 text-[#8A8A8A] text-xs">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-white/30" />
                            {formatDate(item.created_at)}
                          </div>
                        </td>
                        <td className="py-4 px-6 text-right">
                          {isFounding ? (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider text-[#C8A24A] bg-[#C8A24A]/10 border border-[#C8A24A]/30 px-2.5 py-1 rounded-full shadow-[0_2px_10px_rgba(200,162,74,0.05)]">
                              <Award className="w-3 h-3 fill-current" /> Founding Member
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase font-medium tracking-wider text-[#B0B0B0] bg-white/5 border border-white/10 px-2.5 py-1 rounded-full">
                              Standard Member
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
